/**
 * Out-of-office and the departed reply (piece 32; decisions 0119, 0792, 0843):
 * the person's own rule, edited from the mail client, and the open-ended rule a
 * departed person's mailbox answers with. 🔨 A reply goes once per sender per
 * rule, never to a bounce, a mailing list or another auto-reply, and never on a
 * quarantined message; those tests run before the sender table is read.
 */
import { MAIL_API_PATH } from './client';
import { mailMailboxPath } from './grants';
import { addCalendarDays } from '../zonedTime';

export const MAIL_OUT_OF_OFFICE_PATH = `${MAIL_API_PATH}/out-of-office`;
export const mailDepartedReplyPath = (mailboxId: string): string => `${mailMailboxPath(mailboxId)}/departed-reply`;

/** The two template rows the default messages come from, edited in Settings › Email templates like any other. */
export const OUT_OF_OFFICE_TEMPLATE_NAME = 'Out of office';
export const DEPARTED_REPLY_TEMPLATE_NAME = 'Left the company';
/** 🔒 The default message merges the return date (decision 0119). */
export const OUT_OF_OFFICE_DEFAULT_SUBJECT = 'Automatic reply';
export const OUT_OF_OFFICE_DEFAULT_MESSAGE = 'Thank you for your message. I am out of the office and back on {{returnDate}}, and I will answer then.';
/** The departed reply names who to write to instead (piece 32, The departed employee's mailbox). */
export const DEPARTED_REPLY_DEFAULT_MESSAGE = '{{name}} has left {{organization}}. Please write to {{successorName}} at {{successorEmail}}, who will be glad to help.';
/** The subject every automatic reply carries before the original's. */
export const AUTO_REPLY_SUBJECT_PREFIX = 'Automatic reply:';
/** RFC 3834: the header an automatic reply carries, which another responder reads as its cue to stay silent. */
export const AUTO_SUBMITTED_HEADER = 'Auto-Submitted';
export const AUTO_REPLIED = 'auto-replied';

/** The person's own rule as the Out of office window edits it. */
export interface OutOfOfficeRuleView {
  id: string;
  version: number;
  /** Instants, drawn and typed on the viewer's own wall clock. */
  startsAt: string;
  endsAt: string | null;
  message: string;
  isActive: boolean;
}

export interface OutOfOfficeAnswer {
  rule: OutOfOfficeRuleView | null;
  /** 🔒 `mail.outOfOfficeFromLeave`, the person's own opt-in over the organisation's default of off (decision 0792). */
  fromLeave: boolean;
  /** The message a new rule starts from: the template row's, with `{{returnDate}}` left to merge at each reply. */
  defaultMessage: string;
}

export interface OutOfOfficeInput {
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  message: string;
  fromLeave?: boolean;
  version?: number;
}

/** The departed reply on a departed mailbox's record page. */
export interface DepartedReplyView {
  rule: { id: string; version: number; message: string; isActive: boolean; startsAt: string } | null;
  /** The template's message with the departed person's name and the organisation merged, the successor's left to choose. */
  defaultMessage: string;
  /** A super admin writes it (decision 0843). */
  mayWrite: boolean;
}

export interface DepartedReplyInput {
  successorEmploymentId?: string | null;
  message: string;
  isActive: boolean;
  version?: number;
}

const MAX_MESSAGE = 5000;

/** What is wrong with an out-of-office rule, in words, or null: a start before its end, and a message. */
export function outOfOfficeProblem(input: Partial<OutOfOfficeInput>): string | null {
  if (!input.startsAt || Number.isNaN(Date.parse(input.startsAt))) return 'choose the first day away';
  if (!input.endsAt || Number.isNaN(Date.parse(input.endsAt))) return 'choose the last day away';
  if (Date.parse(input.endsAt) <= Date.parse(input.startsAt)) return 'the last day away cannot be before the first';
  if (!input.message || !input.message.trim()) return 'write the message senders receive';
  if (input.message.length > MAX_MESSAGE) return `the message is at most ${MAX_MESSAGE} characters`;
  return null;
}

/** The facts first sight reads before it answers a message. */
export interface AutoReplyFacts {
  isBounce: boolean;
  quarantined: boolean;
  autoSubmitted: string | null;
  precedence: string | null;
  listId: string | null;
  listUnsubscribe: string | null;
  returnPath: string | null;
  /** Any of X-Autoreply, X-Autorespond or X-Auto-Response-Suppress, which responders set on their own mail. */
  responderHeader: boolean;
  fromAddress: string;
  mailboxAddress: string;
}

export type AutoReplySkip = 'quarantined' | 'bounce' | 'autoReply' | 'list' | 'automatedSender' | 'self';

/**
 * 🔨 Why an automatic reply is not sent, or null (piece 32, Out-of-office; decision 0119): never on a quarantined
 * message, never to a bounce, another auto-reply or a mailing list, and never to an automated sender such as a
 * no-reply address. These run before the sender table, so two responders never answer each other.
 */
export function autoReplySkipReason(facts: AutoReplyFacts): AutoReplySkip | null {
  if (facts.quarantined) return 'quarantined';
  if (facts.isBounce) return 'bounce';
  if (facts.returnPath !== null && /^\s*<\s*>\s*$/.test(facts.returnPath)) return 'bounce';
  if (facts.autoSubmitted !== null && !/^\s*no\b/i.test(facts.autoSubmitted)) return 'autoReply';
  if (facts.responderHeader) return 'autoReply';
  if (facts.listId !== null || facts.listUnsubscribe !== null) return 'list';
  if (facts.precedence !== null && /\b(bulk|list|junk|auto_reply)\b/i.test(facts.precedence)) return 'list';
  const local = facts.fromAddress.split('@')[0]?.toLowerCase() ?? '';
  if (/^(mailer-daemon|postmaster|no-?reply|do-?not-?reply|noreply-.*|bounces?(\+.*)?)$/.test(local)) return 'automatedSender';
  if (facts.fromAddress.toLowerCase() === facts.mailboxAddress.toLowerCase()) return 'self';
  return null;
}


/**
 * 🔒 The return date the default message merges (decision 0115): the first working day on or after the day the
 * rule ends, read from the Holiday and BusinessHours rows the caller turns into `isWorkingDay`.
 */
export function returnDay(endsOnDay: string, isWorkingDay: (iso: string) => boolean): string {
  let day = endsOnDay;
  for (let i = 0; i < 60 && !isWorkingDay(day); i += 1) day = addCalendarDays(day, 1);
  return day;
}

/** A calendar date written out for a sender, such as Monday, October 5, 2026. */
export function returnDateText(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));
}

/** Each `{{token}}` a message names, replaced by its fact; a token with no fact is left as written. */
export function mergeReplyTokens(message: string, facts: Readonly<Record<string, string>>): string {
  return message.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (whole, token: string) => (facts[token] !== undefined ? facts[token]! : whole));
}
